/** Build query params for the pending applications document export. */
export const build_pending_documents_zip_params = (
  project_id,
  entity_type_id,
  route_query,
) =>
  Object.fromEntries(
    [
      ["project_id", Number(project_id)],
      ["entity_type_id", entity_type_id],
      ["entity_type_name", route_query.entity_type_name],
      ["status_id", route_query.status_id],
      ["status", route_query.status],
      ["advert_id", route_query.advert_id],
      ["merge_documents", route_query.merge_documents],
    ].filter(([, value]) => value),
  );

/** Download the pending applications zip and save it through the browser. */
export async function download_pending_documents_zip(
  axios_client,
  endpoint_url,
  headers,
  params,
  on_download_progress = null,
) {
  const response = await axios_client.get(endpoint_url, {
    headers,
    params,
    responseType: "blob",
    ...(on_download_progress ? { onDownloadProgress: on_download_progress } : {}),
  });
  await assert_document_download_response(response);
  save_pending_documents_download(response, params);
}

/** Resolve API or blob error payloads into one message string. */
export async function resolve_pending_zip_error_message(response_data) {
  if (response_data instanceof Blob) {
    return resolve_blob_error_message(response_data);
  }
  return response_data?.message || "Failed to download pending documents.";
}

/** Save the zip blob returned by the backend as an explicit download. */
function save_pending_documents_download(response, params) {
  const headers = response.headers || {};
  const disposition = Reflect.get(headers, "content-disposition") || "";
  const file_name = extract_download_file_name(disposition, params);
  const blob_url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = blob_url;
  link.setAttribute("download", file_name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blob_url);
}

/** Throw the backend message when a blob response is not a zip attachment. */
async function assert_document_download_response(response) {
  const response_headers = response.headers || {};
  const content_type = Reflect.get(response_headers, "content-type") || "";
  const is_zip = content_type.includes("application/zip");
  const is_pdf = content_type.includes("application/pdf");
  if (is_zip || is_pdf) {
    return;
  }

  throw new Error(await resolve_pending_zip_error_message(response.data));
}

/** Extract the zip filename from a Content-Disposition header. */
function extract_download_file_name(content_disposition, params) {
  const encoded_match = content_disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded_match?.[1]) {
    return decodeURIComponent(encoded_match[1]);
  }

  const plain_match = content_disposition.match(/filename="?([^";]+)"?/i);
  return plain_match?.[1] || build_pending_download_fallback_name(params);
}

/** Build a useful zip filename when response headers are not readable. */
function build_pending_download_fallback_name(params) {
  const status_name = sanitize_file_name_part(
    Reflect.get(params || {}, "status"),
    "Selected Status",
  );
  const is_merged_pdf = Reflect.get(params || {}, "merge_documents");
  return is_merged_pdf
    ? `${status_name} - Merged Documents.pdf`
    : `${status_name}.zip`;
}

/** Sanitize one filename segment. */
function sanitize_file_name_part(value, fallback) {
  const raw_value = String(value || "").trim();
  const safe_value = raw_value.replace(/[<>:"/\\|?*]/g, "_");
  return safe_value || fallback;
}

/** Parse a JSON error response that arrived as a blob download payload. */
async function resolve_blob_error_message(blob) {
  const text = await blob.text();
  const json_message = parse_json_blob_message(text);
  if (json_message) {
    return json_message;
  }
  return resolve_non_json_blob_message(text);
}

/** Parse backend JSON error text from a blob payload. */
function parse_json_blob_message(text) {
  try {
    const payload = JSON.parse(text);
    return payload.message || payload.detail || payload.error || "";
  } catch {
    return "";
  }
}

/** Explain common non-JSON download failures. */
function resolve_non_json_blob_message(text) {
  const normalized_text = text.toLowerCase();
  const is_frontend_shell = (
    normalized_text.includes("<!doctype html")
    || normalized_text.includes("without javascript enabled")
  );
  if (is_frontend_shell) {
    return "We could not prepare the document download. Please try again.";
  }
  return "Failed to download pending documents.";
}
